// Layer 4 (UI) — the sign-off matrix's column header (locked comp
// `.impeccable/mocks/decision/documents.html` B, adjudication 1): the
// training-material currency table retired into it. Each document column
// names its id, its published version and, while a revision is in review, the
// version that revision publishes as; its currency status is the header's
// title, and a header click opens that document on the Procedure library, as
// the table's row click did. Renders `TrainingCompliance.transform.ts` and
// `TrainingCurrency.transform.ts`; derives nothing.

import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import type { SignoffMatrix } from './TrainingCompliance.transform';
import { CURRENCY_LABEL, trainingCurrencyRows } from './TrainingCurrency.transform';

export type TrainingCurrencyProps = {
  matrix: SignoffMatrix;
  /** A column header click opens that document on the Procedure library tab. */
  onSelectDocument: (id: string) => void;
};

const HEAD = 'px-1 text-center align-middle text-[10px] font-semibold uppercase tracking-[0.07em] text-muted';

export function TrainingCurrency({ matrix, onSelectDocument }: TrainingCurrencyProps) {
  const currency = new Map(trainingCurrencyRows().map((row) => [row.id, row] as const));

  return (
    <Commentable name="TrainingCurrency">
      <thead>
        <tr>
          <th aria-hidden className="border-b border-[var(--hairline)]" />
          <th aria-hidden className="border-b border-[var(--hairline)]" />
          {matrix.groups.map((group) => (
            <th
              key={group.label}
              colSpan={group.span}
              className={cn(HEAD, 'whitespace-nowrap border-b border-l border-[var(--hairline)] border-l-[var(--color-divider)] px-0.5 pb-1 pt-1.5 tracking-[0.05em]')}
            >
              {group.label}
            </th>
          ))}
          <th aria-hidden colSpan={2} className="border-b border-l border-[var(--hairline)] border-l-[var(--color-divider)]" />
        </tr>
        <tr>
          <th className={cn(HEAD, 'border-b border-[var(--color-divider)] py-1.5 pl-4 text-left')}>Name · role</th>
          <th className={cn(HEAD, 'border-b border-[var(--color-divider)] px-px py-1.5')}>Panel</th>
          {matrix.columns.map((column) => {
            const status = currency.get(column.id)?.status;
            const label = `${column.title} · ${column.version}${status ? ` · ${CURRENCY_LABEL[status]}` : ''}`;
            return (
              <th
                key={column.id}
                data-matrix-column={column.id}
                className={cn(
                  'border-b border-[var(--color-divider)] p-0 align-middle',
                  column.groupStart ? 'border-l border-l-[var(--color-divider)]' : '',
                )}
              >
                <button
                  type="button"
                  title={label}
                  aria-label={`Open ${column.id}: ${label}`}
                  onClick={() => onSelectDocument(column.id)}
                  className="flex w-full flex-col items-center px-0.5 py-1.5 leading-[1.2] transition-colors duration-150 hover:bg-[var(--surface-1)]"
                >
                  <span
                    className={cn(
                      'whitespace-nowrap font-mono text-[11px] tracking-normal',
                      column.criticalSop ? 'font-semibold text-ink-primary' : 'font-medium text-ink-secondary',
                    )}
                  >
                    {column.id}
                  </span>
                  <span className="whitespace-nowrap text-[10px] font-medium text-muted">
                    {column.version}
                    {column.pendingVersion ? (
                      <span
                        data-matrix-pending={column.id}
                        className="ml-0.5 rounded-[4px] border border-dashed border-[var(--hairline-strong)] px-[3px]"
                      >
                        {column.pendingVersion}
                      </span>
                    ) : null}
                  </span>
                </button>
              </th>
            );
          })}
          <th className={cn(HEAD, 'whitespace-nowrap border-b border-l border-[var(--color-divider)] py-1.5 pl-1 pr-0 text-left tracking-[0.05em]')}>Compliance %</th>
          <th className={cn(HEAD, 'border-b border-[var(--color-divider)] py-1.5 pr-2 text-right leading-tight')}>Overdue items</th>
        </tr>
      </thead>
    </Commentable>
  );
}
